import type { Socket } from "socket.io-client";
import { io } from "socket.io-client";
import type { Editor, Message, NewMessageInterface } from "@/model/csr-chat";
import {
  CSR_MESSAGE_STATUS_SEEN,
  CSR_SOCKET_RECONNECTION_ATTEMPTS,
  CSR_SOCKET_TIMEOUT_MS,
} from "./csr-chat-constants";
import type { ChangeChannelStatus } from "@/model/csr-chat";

export type { Editor, Message, NewMessageInterface };
export { MessageType } from "@/model/csr-chat";

const CSR_CHAT_SOCKET_URL =
  process.env.CSR_CHAT_SOCKET_URL ?? process.env.CSR_CHAT ?? "";

export enum ChatEvent {
  CONNECT = "connect",
  DISCONNECT = "disconnect",
  ECHO = "echo",
  USER_STATUS_CHANGE = "userStatusChange",
  USER_TYPING_STATUS = "userTypingStatus",
  NEW_CHANNEL = "newChannel",
  NEW_MESSAGE = "newMessage",
  MESSAGE_STATUS_CHANGE = "messageStatusChange",
  CHANNEL_STATUS_UPDATE = "channelStatusUpdate",
}

export type SocketHeaders = {
  app_id: string;
  username: string;
  group_id: string;
  division_id: string;
  first_name: string;
  last_name: string;
  phone_no: string;
  member_id: string;
  user_role: string;
  user_type: string;
  device_token: string;
  os: string;
  model: string;
  brand: string;
  app_version: string;
  user_agent: string;
  system_version: string;
  build_number: string;
  group_name: string;
  email: string;
  icon: string;
  mbr_id: string;
};

export type MessageStatusChangePayload = {
  messageId: string;
  chatChannel: string;
  status: "seen" | "delivered" | "sent";
};

export type NewChannelPayload = {
  channelId: string;
  channelName: string;
};

export type UserStatusChangePayload = {
  userId: string;
  status: "online" | "offline";
};

export type UserTypingStatusPayload = {
  userId: string;
  chatChannel: string;
  isTyping: boolean;
  editor?: Editor;
};

export type SocketEventHandlers = {
  onConnect?: (socketId: string) => void;
  onDisconnect?: (reason: string) => void;
  onEcho?: (data: unknown) => void;
  onNewMessage?: (data: Message) => void;
  onNewChannel?: (data: NewChannelPayload) => void;
  onUserStatusChange?: (data: UserStatusChangePayload) => void;
  onMessageStatusChange?: (data: MessageStatusChangePayload) => void;
  onUserTypingStatus?: (data: UserTypingStatusPayload) => void;
};

let socket: Socket | null = null;
let activeHandlers: SocketEventHandlers = {};

export const connectSocket = (
  token: string,
  headers: SocketHeaders,
): Socket => {
  if (socket) return socket;

  socket = io(CSR_CHAT_SOCKET_URL, {
    extraHeaders: {
      ...headers,
      authorization: token,
    },
    reconnectionAttempts: CSR_SOCKET_RECONNECTION_ATTEMPTS,
    timeout: CSR_SOCKET_TIMEOUT_MS,
    reconnection: true,
  });

  registerEvents(socket);
  return socket;
};

export const updateSocketHandlers = (handlers: SocketEventHandlers): void => {
  activeHandlers = handlers;
};

export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  activeHandlers = {};
};

export const emitNewMessage = (payload: NewMessageInterface): void => {
  socket?.emit(ChatEvent.NEW_MESSAGE, payload);
};
export const emitChannelStatusChange = (payload: ChangeChannelStatus): void => {
  socket?.emit(ChatEvent.CHANNEL_STATUS_UPDATE, payload);
};

export const emitMessageStatusChange = (
  payload: MessageStatusChangePayload,
): void => {
  socket?.emit(ChatEvent.MESSAGE_STATUS_CHANGE, payload);
};

export const emitMessageSeenStatus = (
  messageId: string,
  chatChannel: string,
): void => {
  emitMessageStatusChange({
    messageId,
    chatChannel,
    status: CSR_MESSAGE_STATUS_SEEN,
  });
};

export const emitUserTypingStatus = (payload: {
  chatChannel: string;
  isTyping: boolean;
}): void => {
  socket?.emit(ChatEvent.USER_TYPING_STATUS, payload);
};

export const getSocket = (): Socket | null => socket;

const registerEvents = (socket: Socket): void => {
  socket.on(ChatEvent.CONNECT, () => {
    activeHandlers.onConnect?.(socket.id ?? "");
  });

  socket.on(ChatEvent.DISCONNECT, (reason: string) => {
    activeHandlers.onDisconnect?.(reason);
  });

  socket.on(ChatEvent.ECHO, (data: unknown) => {
    activeHandlers.onEcho?.(data);
  });

  socket.on(ChatEvent.NEW_MESSAGE, (data: Message) => {
    activeHandlers.onNewMessage?.(data);
  });

  socket.on(ChatEvent.NEW_CHANNEL, (data: NewChannelPayload) => {
    activeHandlers.onNewChannel?.(data);
  });

  socket.on(ChatEvent.USER_STATUS_CHANGE, (data: UserStatusChangePayload) => {
    activeHandlers.onUserStatusChange?.(data);
  });

  socket.on(
    ChatEvent.MESSAGE_STATUS_CHANGE,
    (data: MessageStatusChangePayload) => {
      activeHandlers.onMessageStatusChange?.(data);
    },
  );

  socket.on(ChatEvent.USER_TYPING_STATUS, (data: UserTypingStatusPayload) => {
    activeHandlers.onUserTypingStatus?.(data);
  });
};
